/**
 * KeyFeatureEditor - Structured key feature entries with type-specific forms (Plan 97/B7).
 *
 * Types: placeholder, binary_choice, conditional, note.
 * Each type has its own mini-form. Add/remove/reorder entries.
 */

import React, { useState, useCallback } from 'react';
import type { KeyFeatureItem } from '../../types/playbook';

interface KeyFeatureEditorProps {
  features: KeyFeatureItem[];
  onChange: (features: KeyFeatureItem[]) => void;
}

const TYPE_LABELS: Record<KeyFeatureItem['type'], string> = {
  placeholder: 'Placeholder',
  binary_choice: 'Binary Choice',
  conditional: 'Conditional',
  note: 'Note',
};

const FEATURE_TYPES = Object.keys(TYPE_LABELS) as KeyFeatureItem['type'][];

function createDefault(type: KeyFeatureItem['type']): KeyFeatureItem {
  switch (type) {
    case 'placeholder':
      return { type: 'placeholder', label: '', description: '' };
    case 'binary_choice':
      return { type: 'binary_choice', label: '', option_a: '', option_b: '', default: '' };
    case 'conditional':
      return { type: 'conditional', condition: '', then: '' };
    case 'note':
      return { type: 'note', text: '' };
  }
}

export function KeyFeatureEditor({ features, onChange }: KeyFeatureEditorProps) {
  const [showTypePicker, setShowTypePicker] = useState(false);

  const addFeature = useCallback(
    (type: KeyFeatureItem['type']) => {
      onChange([...features, createDefault(type)]);
      setShowTypePicker(false);
    },
    [features, onChange],
  );

  const updateFeature = useCallback(
    (index: number, updated: KeyFeatureItem) => {
      const next = [...features];
      next[index] = updated;
      onChange(next);
    },
    [features, onChange],
  );

  const removeFeature = useCallback(
    (index: number) => {
      onChange(features.filter((_, i) => i !== index));
    },
    [features, onChange],
  );

  return (
    <div className="pb-kf">
      {features.map((feature, i) => (
        <div key={i} className="pb-kf__item">
          <div className="pb-kf__item-header">
            <span className="pb-kf__type-badge">{TYPE_LABELS[feature.type]}</span>
            <button
              className="pb-kf__remove"
              onClick={() => removeFeature(i)}
              title="Remove"
            >
              &times;
            </button>
          </div>
          <FeatureFields feature={feature} onChange={(f) => updateFeature(i, f)} />
        </div>
      ))}

      <div className="pb-kf__add-wrapper">
        {showTypePicker ? (
          <div className="pb-kf__type-picker">
            {FEATURE_TYPES.map((type) => (
              <button
                key={type}
                className="pb-kf__type-option"
                onClick={() => addFeature(type)}
              >
                {TYPE_LABELS[type]}
              </button>
            ))}
            <button
              className="pb-kf__type-option pb-kf__type-option--cancel"
              onClick={() => setShowTypePicker(false)}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            className="pb-btn pb-btn--ghost pb-btn--sm"
            onClick={() => setShowTypePicker(true)}
          >
            + Add feature
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Type-specific field editors
// ---------------------------------------------------------------------------

function FeatureFields({
  feature,
  onChange,
}: {
  feature: KeyFeatureItem;
  onChange: (f: KeyFeatureItem) => void;
}) {
  switch (feature.type) {
    case 'placeholder':
      return (
        <div className="pb-kf__fields">
          <input
            className="pb-form-input pb-form-input--sm"
            placeholder="Label"
            value={feature.label}
            onChange={(e) => onChange({ ...feature, label: e.target.value })}
          />
          <input
            className="pb-form-input pb-form-input--sm"
            placeholder="Description (e.g. Typically 1x-2x annual fees)"
            value={feature.description ?? ''}
            onChange={(e) => onChange({ ...feature, description: e.target.value })}
          />
        </div>
      );
    case 'binary_choice':
      return (
        <div className="pb-kf__fields">
          <input
            className="pb-form-input pb-form-input--sm"
            placeholder="Label"
            value={feature.label}
            onChange={(e) => onChange({ ...feature, label: e.target.value })}
          />
          <div className="pb-kf__row">
            <input
              className="pb-form-input pb-form-input--sm"
              placeholder="Option A"
              value={feature.option_a}
              onChange={(e) => onChange({ ...feature, option_a: e.target.value })}
            />
            <input
              className="pb-form-input pb-form-input--sm"
              placeholder="Option B"
              value={feature.option_b}
              onChange={(e) => onChange({ ...feature, option_b: e.target.value })}
            />
          </div>
          <select
            className="pb-form-input pb-form-input--sm"
            value={feature.default ?? ''}
            onChange={(e) => onChange({ ...feature, default: e.target.value || undefined })}
          >
            <option value="">Default: none</option>
            <option value={feature.option_a || 'A'}>{feature.option_a || 'Option A'}</option>
            <option value={feature.option_b || 'B'}>{feature.option_b || 'Option B'}</option>
          </select>
        </div>
      );
    case 'conditional':
      return (
        <div className="pb-kf__fields">
          <input
            className="pb-form-input pb-form-input--sm"
            placeholder="Condition (e.g. Deal value exceeds $1M)"
            value={feature.condition}
            onChange={(e) => onChange({ ...feature, condition: e.target.value })}
          />
          <input
            className="pb-form-input pb-form-input--sm"
            placeholder="Then (action or clause to add)"
            value={feature.then}
            onChange={(e) => onChange({ ...feature, then: e.target.value })}
          />
        </div>
      );
    case 'note':
      return (
        <div className="pb-kf__fields">
          <textarea
            className="pb-form-input pb-form-input--sm pb-form-textarea"
            placeholder="Note text"
            value={feature.text}
            onChange={(e) => onChange({ ...feature, text: e.target.value })}
            rows={2}
          />
        </div>
      );
  }
}
